import * as C from "../../constants";

const initialState = {
    initialized: false,
    isLoading: false,
    accountFlagTypes: [],
    accounts: {},
    successMessage: "",
    errorMessage: "",
    showSnackBar: false,
    expandedViews: [],
};

const loadAccountFlagTypes = (state, action) => {
    return {
        ...state,
        accountFlagTypes: action.accountFlagTypes,
    };
};

const setIsLoading = (state, action) => ({
    ...state,
    isLoading: action.isLoading,
});

const loadAccountFlags = (state, action) => {
    return {
        ...state,
        accounts: {
            ...state.accounts,
            // guard against Items: null (empty slice serialized as null).
            // reason: null = untouched, "" = explicitly cleared. *_original
            // is the loaded baseline, used to detect an edit typed-then-undone.
            [action.selectedAccountID]: (action.accountFlags || []).map((f) => ({
                ...f,
                reason: f.reason || null,
                reason_original: f.reason || null,
                expired_original: f.expired || null,
            })),
        },
    };
};

const addAccountFlag = (state, action) => {
    // /create upserts by (account_id, flag_type) server-side - replace by
    // flag_type rather than always appending, or a reason/expiry-only edit
    // on an existing flag duplicates the row.
    const existingFlags = state.accounts[action.selectedAccountID] || [];
    const accountFlag = action.accountFlag || {};
    const nextFlag = {
        ...accountFlag,
        reason: accountFlag.reason || null,
        reason_original: accountFlag.reason || null,
        expired_original: accountFlag.expired || null,
    };
    const existingIndex = existingFlags.findIndex((f) => f.flag_type === nextFlag.flag_type);
    const accountFlags = existingIndex === -1
        ? [...existingFlags, nextFlag]
        : existingFlags.map((f, i) => (i === existingIndex ? nextFlag : f));

    return {
        ...state,
        accounts: {
            ...state.accounts,
            [action.selectedAccountID]: accountFlags,
        },
    };
};

const deleteAccountFlag = (state, action) => {
    const index = state.accounts[action.selectedAccountID].findIndex(
        (f) => f.account_flag_id === action.accountFlag.account_flag_id
    );

    return {
        ...state,
        accounts: {
            ...state.accounts,
            [action.selectedAccountID]: [
                ...state.accounts[action.selectedAccountID].slice(0, index),
                ...state.accounts[action.selectedAccountID].slice(index + 1),
            ],
        },
    };
};

const setSuccess = (state, action) => {
    return {
        ...state,
        showSnackBar: true,
        successMessage: action.message,
        // must not coexist with a stale errorMessage, or a later error gets
        // masked behind a green toast (container styles off successMessage).
        errorMessage: "",
    };
};

const setError = (state, action) => {
    return {
        ...state,
        showSnackBar: true,
        successMessage: "",
        errorMessage: action.message,
    };
};

const dismissMessage = (state) => {
    return {
        ...state,
        showSnackBar: false,
        successMessage: "",
        errorMessage: "",
    };
};

const toggleCategoryView = (state, action) => {
    const index = state.expandedViews.indexOf(action.category);
    let expandedViews;
    if (index === -1) {
        expandedViews = [action.category];
    } else {
        expandedViews = [];
    }
    return {
        ...state,
        expandedViews,
    };
};

const toggleAccountFlag = (state, action) => {
    const existingFlags = state.accounts[action.selectedAccountID] || [];
    const accountFlag = existingFlags.find((a) => a.flag_type === action.flagId);
    const accountIndex = existingFlags.indexOf(accountFlag);

    let accountFlags = existingFlags;
    if (action.userAction === "toggleAccountFlag") {
        if (!accountFlag) {
            accountFlags = [
                ...existingFlags,
                { flag_type: action.flagId, action: "add", expired: action.expired, reason: action.reason ?? null },
            ];
        } else {
            accountFlags = existingFlags.map((f, i) =>
                i === accountIndex
                    ? { ...f, action: f.action !== "remove" ? "remove" : "add" }
                    : f
            );
        }
    } else if (action.userAction === "expirationDateUpdated") {
        if (accountFlag) {
            accountFlags = existingFlags.map((f, i) =>
                i === accountIndex ? { ...f, expired: action.expired, action: "add" } : f
            );
        }
    } else if (action.userAction === "reasonUpdated") {
        if (accountFlag) {
            accountFlags = existingFlags.map((f, i) => {
                if (i !== accountIndex) return f;
                const updated = { ...f, reason: action.reason };
                // typing then deleting shouldn't leave the row dirty - but
                // only for a flag that already existed and was only dirty
                // from this edit; a freshly-checked flag stays dirty either way.
                const backToOriginal =
                    f.account_flag_id &&
                    f.action === "add" &&
                    updated.reason === (f.reason_original ?? null) &&
                    updated.expired === (f.expired_original ?? null);
                updated.action = backToOriginal ? null : "add";
                return updated;
            });
        }
    }

    return {
        ...state,
        accounts: {
            ...state.accounts,
            [action.selectedAccountID]: accountFlags,
        },
    };
};

export const cancelChanges = (state, action) => {
    const accountFlags = (state.accounts[action.selectedAccountID] || [])
        .filter((af) => af.account_flag_id)
        .map((a) => ({ ...a, action: null }));
    return {
        ...state,
        accounts: {
            ...state.accounts,
            [action.selectedAccountID]: accountFlags,
        },
    };
};
export default function accountFlags(state = initialState, action) {
    switch (action.type) {
        case C.ACCOUNT_FLAG_TYPES_LOAD:
            return loadAccountFlagTypes(state, action);
        case C.ACCOUNT_FLAG_SET_LOADING:
            return setIsLoading(state, action);
        case C.ACCOUNT_FLAGS_LOAD:
            return loadAccountFlags(state, action);
        case C.ACCOUNT_FLAGS_ADD:
            return addAccountFlag(state, action);
        case C.ACCOUNT_FLAGS_DELETE:
            return deleteAccountFlag(state, action);
        case C.ACCOUNT_FLAG_SET_SUCCESS:
            return setSuccess(state, action);
        case C.ACCOUNT_FLAG_SET_ERROR:
            return setError(state, action);
        case C.ACCOUNT_FLAG_DISMISS_MESSAGE:
            return dismissMessage(state);
        case C.ACCOUNT_FLAG_TOGGLE_CATEGORY_VIEW:
            return toggleCategoryView(state, action);
        case C.ACCOUNT_FLAG_TOGGLE:
            return toggleAccountFlag(state, action);
        case C.ACCOUNT_FLAG_CANCEL_CHANGES:
            return cancelChanges(state, action);
        default:
            return state;
    }
}
